import { gql } from '@apollo/client'

export const MY_PROVIDER_MANAGEMENT_QUERY = gql`
  query MyProviderManagement {
    myProviderProfile {
      id
      bio
      yearsExperience
      languages
      hourlyRateCents
      acceptingJobs
      instantBooking
      maxJobsPerDay
      bufferMinutes
      verified
      verificationStatus
      serviceOfferings {
        id
        customPriceCents
        customDurationMinutes
        active
        serviceType {
          id
          name
          nameEl
          translationKey
          basePriceCents
          estimatedDurationMinutes
          serviceCategory {
            id
            name
            nameEl
            translationKey
          }
        }
      }
    }
  }
`

export const MY_PROVIDER_PUBLIC_AVAILABILITY_QUERY = gql`
  query MyProviderPublicAvailability($id: ID!, $from: ISO8601Date!, $to: ISO8601Date!) {
    providerProfile(id: $id) {
      id
      availabilitySlots {
        id
        dayOfWeek
        dayName
        startTime
        endTime
      }
      availableSlots(from: $from, to: $to) {
        date
        startTime
        endTime
        source
      }
    }
  }
`

export const MY_PROVIDER_PUBLIC_AREAS_QUERY = gql`
  query MyProviderPublicAreas($id: ID!) {
    providerProfile(id: $id) {
      id
      serviceAreas {
        id
        postalCode
        areaName
        city
      }
    }
  }
`

export const UPDATE_PROVIDER_PROFILE_MUTATION = gql`
  mutation UpdateManagedProviderProfile(
    $bio: String
    $yearsExperience: Int
    $languages: [String!]
    $hourlyRateCents: Int
    $acceptingJobs: Boolean
    $instantBooking: Boolean
    $maxJobsPerDay: Int
    $bufferMinutes: Int
  ) {
    updateProviderProfile(input: {
      bio: $bio
      yearsExperience: $yearsExperience
      languages: $languages
      hourlyRateCents: $hourlyRateCents
      acceptingJobs: $acceptingJobs
      instantBooking: $instantBooking
      maxJobsPerDay: $maxJobsPerDay
      bufferMinutes: $bufferMinutes
    }) {
      providerProfile {
        id
        bio
        yearsExperience
        languages
        hourlyRateCents
        acceptingJobs
        instantBooking
        maxJobsPerDay
        bufferMinutes
      }
      errors
    }
  }
`

export const UPDATE_USER_AVATAR_MUTATION = gql`
  mutation UpdateManagedUserAvatar($avatarUrl: String) {
    updateProfile(input: { avatarUrl: $avatarUrl }) {
      user {
        id
        avatarUrl
      }
      errors
    }
  }
`

export const SET_PROVIDER_SERVICES_MUTATION = gql`
  mutation SetManagedProviderServices($services: [ServiceInput!]!) {
    setProviderServices(input: { services: $services }) {
      providerProfile {
        id
        serviceOfferings {
          id
          customPriceCents
          customDurationMinutes
          active
          serviceType {
            id
            name
            nameEl
            translationKey
            basePriceCents
            estimatedDurationMinutes
            serviceCategory {
              id
              name
              nameEl
              translationKey
            }
          }
        }
      }
      errors
    }
  }
`

export const SET_WEEKLY_AVAILABILITY_MUTATION = gql`
  mutation SetManagedWeeklyAvailability($slots: [AvailabilitySlotInput!]!) {
    setWeeklyAvailability(input: { slots: $slots }) {
      slots {
        id
        dayOfWeek
        dayName
        startTime
        endTime
      }
      errors
    }
  }
`

export const SET_AVAILABILITY_OVERRIDE_MUTATION = gql`
  mutation SetManagedAvailabilityOverride(
    $date: String!
    $available: Boolean!
    $startTime: String
    $endTime: String
    $reason: String
  ) {
    setAvailabilityOverride(input: {
      input: {
        date: $date
        available: $available
        startTime: $startTime
        endTime: $endTime
        reason: $reason
      }
    }) {
      override {
        id
        date
        available
        startTime
        endTime
        reason
      }
      errors
    }
  }
`

export const SET_SERVICE_AREAS_MUTATION = gql`
  mutation SetManagedServiceAreas($areas: [AreaInput!]!) {
    setServiceAreas(input: { areas: $areas }) {
      serviceAreas {
        id
        postalCode
        areaName
        city
      }
      errors
    }
  }
`

export interface ManagedServiceCategory {
  id: string
  name: string
  nameEl: string
  translationKey: string
}

export interface ManagedServiceType {
  id: string
  name: string
  nameEl: string
  translationKey: string
  basePriceCents: number
  estimatedDurationMinutes: number
  serviceCategory: ManagedServiceCategory
}

export interface ManagedServiceOffering {
  id: string
  customPriceCents: number | null
  customDurationMinutes: number | null
  active: boolean
  serviceType: ManagedServiceType
}

export interface ManagedProviderProfile {
  id: string
  bio: string | null
  yearsExperience: number | null
  languages: string[]
  hourlyRateCents: number | null
  acceptingJobs: boolean
  instantBooking: boolean
  maxJobsPerDay: number
  bufferMinutes: number
  verified: boolean
  verificationStatus: string
  serviceOfferings: ManagedServiceOffering[]
}

export interface MyProviderManagementData {
  myProviderProfile: ManagedProviderProfile | null
}

export interface ManagedServiceArea {
  id: string
  postalCode: string
  areaName: string | null
  city: string
}

export interface ManagedAvailabilitySlot {
  id: string
  dayOfWeek: number
  dayName: string
  startTime: string
  endTime: string
}

export interface ManagedAvailableSlot {
  date: string
  startTime: string
  endTime: string
  source: string
}

export interface ManagedPublicAvailabilityData {
  providerProfile: {
    id: string
    availabilitySlots: ManagedAvailabilitySlot[]
    availableSlots: ManagedAvailableSlot[]
  } | null
}

export interface ManagedPublicAvailabilityVariables {
  id: string
  from: string
  to: string
}

export interface ManagedPublicAreasData {
  providerProfile: {
    id: string
    serviceAreas: ManagedServiceArea[]
  } | null
}

export interface ManagedPublicAreasVariables {
  id: string
}

export interface UpdateProviderProfileData {
  updateProviderProfile: {
    providerProfile: ManagedProviderProfile | null
    errors: string[]
  }
}

export interface UpdateProviderProfileVariables {
  bio?: string
  yearsExperience?: number
  languages?: string[]
  hourlyRateCents?: number
  acceptingJobs?: boolean
  instantBooking?: boolean
  maxJobsPerDay?: number
  bufferMinutes?: number
}

export interface UpdateUserAvatarData {
  updateProfile: {
    user: {
      id: string
      avatarUrl: string | null
    } | null
    errors: string[]
  }
}

export interface UpdateUserAvatarVariables {
  avatarUrl?: string
}

export interface SetProviderServicesData {
  setProviderServices: {
    providerProfile: {
      id: string
      serviceOfferings: ManagedServiceOffering[]
    } | null
    errors: string[]
  }
}

export interface SetProviderServicesVariables {
  services: Array<{
    serviceTypeId: string
    customPriceCents?: number
    customDurationMinutes?: number
  }>
}

export interface SetWeeklyAvailabilityData {
  setWeeklyAvailability: {
    slots: ManagedAvailabilitySlot[]
    errors: string[]
  }
}

export interface SetWeeklyAvailabilityVariables {
  slots: Array<{
    dayOfWeek: number
    startTime: string
    endTime: string
  }>
}

export interface ManagedAvailabilityOverride {
  id: string
  date: string
  available: boolean
  startTime: string | null
  endTime: string | null
  reason: string | null
}

export interface SetAvailabilityOverrideData {
  setAvailabilityOverride: {
    override: ManagedAvailabilityOverride | null
    errors: string[]
  }
}

export interface SetAvailabilityOverrideVariables {
  date: string
  available: boolean
  startTime?: string
  endTime?: string
  reason?: string
}

export interface SetServiceAreasData {
  setServiceAreas: {
    serviceAreas: ManagedServiceArea[] | null
    errors: string[]
  }
}

export interface SetServiceAreasVariables {
  areas: Array<{
    postalCode: string
    areaName?: string
    city?: string
  }>
}
